import { AD_TYPES, AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES } from "components/core/blocks/AdPlan/constants";
import { css } from "../../../styled-system/css";
import { Wrap } from "../../../styled-system/jsx";
import { vstack } from "../../../styled-system/patterns";
import { NumberInput } from "../../components/fields/base/NumberInput";
import { useAppSelector } from "redux/hooks";
import AudienceMultiplierField from "components/core/blocks/AdPlan/fields/AudienceMultiplierField";
import { Select } from "components/fields/base/Select";
import { Checkbox } from "components/fields/base/Checkbox";

interface BulkAdPlanModifierFormData {
  topOfSearchModifier: number;
  productPagesModifier: number;
  restOfSearchModifier: number;
  amazonBusinessModifier: number;
  audienceBidMultiplierTargetAudienceId?: number | null;
  audienceBidMultiplierTargetAudienceName: string | null;
  audienceBidMultiplier?: number;
  sbOtherPageModifier: number;
  sbProductDetailPageModifier: number;
  sbHomePageModifier: number;
  adType: number;
  updatePlacementModifiers: boolean;
  updateAudienceModifiers: boolean;
}

export const INITIAL_MODIFIER_FORM_DATA: BulkAdPlanModifierFormData = {
  topOfSearchModifier: 0,
  productPagesModifier: 0,
  restOfSearchModifier: 0,
  amazonBusinessModifier: 0,
  audienceBidMultiplierTargetAudienceId: null,
  audienceBidMultiplierTargetAudienceName: null,
  sbProductDetailPageModifier: 0,
  sbOtherPageModifier: 0,
  sbHomePageModifier: 0,
  audienceBidMultiplier: 0,
  adType: AD_TYPES.SPONSORED_PRODUCT.value,
  updatePlacementModifiers: true,
  updateAudienceModifiers: false,
};

interface Props {
  formData: BulkAdPlanModifierFormData;
  setFormData: (data: BulkAdPlanModifierFormData) => void;
}

export const keysToIngoreModifierValidationsFor: (keyof BulkAdPlanModifierFormData)[] = [
  "audienceBidMultiplierTargetAudienceId",
  "audienceBidMultiplierTargetAudienceName",
  "updateAudienceModifiers",
  "updatePlacementModifiers",
];

const BulkAdPlanModifierForm = ({ formData, setFormData }: Props) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const adTypeOptions = [AD_TYPES.SPONSORED_PRODUCT, AD_TYPES.SPONSORED_PRODUCT_AUTO, AD_TYPES.SPONSORED_BRAND_V2];
  const handleChange = <T extends keyof BulkAdPlanModifierFormData>(key: T, value: BulkAdPlanModifierFormData[T]) => {
    setFormData({ ...formData, [key]: value });
  };

  const filteredValues = Object.entries(formData)
    .filter(([key]) => !keysToIngoreModifierValidationsFor.includes(key))
    .map(([, value]) => value);
  const hasInvalidValue = filteredValues.some((value) => isNaN(parseInt(value as string)) || value > 900 || value < 0);

  return (
    <form
      className={vstack({
        alignItems: "stretch",
        width: "100%",
        paddingX: "xlarge",
        overflow: "visible",
      })}
    >
      <p className={css({ textAlign: "center" })}>
        Please specify changes to your placement modifiers using the fields below. Modifiers will be updated for all
        selected media plans of the specified type.
      </p>
      <Wrap gap="xlarge" width="50%">
        <Select
          label={"Media Plan Type"}
          onSelectionChange={(value) => {
            handleChange("adType", value as number);
          }}
          selectedKey={formData.adType}
          items={adTypeOptions}
        />
      </Wrap>
      <h4 className={css({ display: "flex", gap: "small", fontSize: "medium", fontWeight: "bolder" })}>
        Update {Object.values(AD_TYPES).find((adType) => adType.value === formData.adType)?.label} Placement Modifiers{" "}
        <Checkbox
          value={formData.updatePlacementModifiers}
          onChange={(checked) => handleChange("updatePlacementModifiers", checked)}
        />
      </h4>
      <Wrap hidden={!formData.updatePlacementModifiers} gap="xlarge" width={"100%"}>
        {[AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(formData.adType) ?
          <>
            <NumberInput
              onChange={(value) => handleChange("topOfSearchModifier", value)}
              value={formData.topOfSearchModifier}
              label={"Top of Search"}
              placeholder={"Enter amount"}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("productPagesModifier", value)}
              value={formData.productPagesModifier}
              label={"Product Pages"}
              placeholder={"Enter amount"}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("restOfSearchModifier", value)}
              value={formData.restOfSearchModifier}
              label={"Rest of Search"}
              placeholder={"Enter amount"}
              minValue={0}
            />
          </>
        : [AD_TYPES.SPONSORED_BRAND_V2.value].includes(formData.adType) ?
          <>
            <NumberInput
              onChange={(value) => handleChange("sbHomePageModifier", value)}
              value={formData.sbHomePageModifier}
              label={"Amazon Home Page"}
              placeholder={"Enter amount"}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("sbProductDetailPageModifier", value)}
              value={formData.sbProductDetailPageModifier}
              label={"Product Detail Page"}
              placeholder={"Enter amount"}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("sbOtherPageModifier", value)}
              value={formData.sbOtherPageModifier}
              label={"Other Amazon Pages"}
              placeholder={"Enter amount"}
              minValue={0}
            />
          </>
        : null}
        {(
          AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace) &&
          [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(formData.adType)
        ) ?
          <NumberInput
            onChange={(value) => handleChange("amazonBusinessModifier", value)}
            value={formData.amazonBusinessModifier}
            label={"Amazon Business"}
            placeholder={"Enter amount"}
            isDisabled={!formData.updatePlacementModifiers}
            minValue={0}
          />
        : null}
      </Wrap>

      <h4 className={css({ display: "flex", gap: "small", fontSize: "medium", fontWeight: "bolder" })}>
        Update {Object.values(AD_TYPES).find((adType) => adType.value === formData.adType)?.label} Audience Modifiers
        <Checkbox
          value={formData.updateAudienceModifiers}
          onChange={(checked) => handleChange("updateAudienceModifiers", checked)}
        />
      </h4>
      <Wrap className={css({ zIndex: 2000 })} hidden={!formData.updateAudienceModifiers} gap="xlarge" width={"50%"}>
        <AudienceMultiplierField
          state={{ ...formData, adType: formData.adType }}
          onChange={(updates: {
            audienceBidMultiplierTargetAudienceId?: number | null;
            audienceBidMultiplierTargetAudienceName: string | null;
            amazonBusinessModifier?: number;
          }) => {
            setFormData({ ...formData, ...updates });
          }}
          classes={{}}
          errors={[]}
          fieldsToValidate={[]}
          hideTitle={true}
          bulkActionForm={true}
          disableFields={!formData.updateAudienceModifiers}
          flexDirection="row"
        />
      </Wrap>

      {hasInvalidValue ?
        <p className={css({ paddingTop: "medium", color: "red", fontSize: "small", textAlign: "center" })}>
          All modifiers must have a value between 0 and 900
        </p>
      : null}
    </form>
  );
};

export default BulkAdPlanModifierForm;
